import { isPlatformServer, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  inject,
  Input,
  OnDestroy,
  PLATFORM_ID,
  QueryList,
  TemplateRef,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { SwipeDirection, SwipeDirective } from './swipe.directive';
import { SwiperItemDirective } from './swiper-item.directive';

@Component({
  selector: 'lib-swiper',
  templateUrl: './swiper.component.html',
  styleUrls: ['./swiper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [SwipeDirective, NgFor, NgTemplateOutlet, NgIf],
})
export class SwiperComponent implements AfterContentInit, OnDestroy {
  private platformId = inject(PLATFORM_ID);
  private changeDetectorRef = inject(ChangeDetectorRef);
  @ContentChildren(SwiperItemDirective, { read: TemplateRef }) templates: QueryList<TemplateRef<unknown>>;
  @Input() hideArrows: boolean;
  @Input() index = 0;
  offset = 0;
  swiping = true;
  numberOfDots = 1;
  templatesSubscription: Subscription;

  async ngAfterContentInit() {
    this.templatesSubscription = this.templates.changes.subscribe(() => {
      this.index = 0;
      this.updateNumberOfDotsAndIndex();
    });
    this.updateNumberOfDotsAndIndex();
    this.swiping = false;
  }

  updateNumberOfDotsAndIndex() {
    this.numberOfDots = this.calculateNumberOfDots();
    if (this.index >= this.numberOfDots) this.index = this.numberOfDots - 1;
    this.changeDetectorRef.markForCheck();
  }

  calculateNumberOfDots(): number {
    if (isPlatformServer(this.platformId)) return 1;
    if (!this.templates) return;
    let itemsOnScreenAtOnce = 1;
    // Careful, these screen size values are also set in the CSS of this component and should be the same
    // to ensure the SSR looks the same as the browser rendered page
    const width = this.getInnerWidth();
    if (width > 768) itemsOnScreenAtOnce = 2;
    if (width > 1280) itemsOnScreenAtOnce = 3;
    if (width > 1536) itemsOnScreenAtOnce = 4;

    return Math.max(1, Math.max(this.templates.length) - itemsOnScreenAtOnce + 1);
  }

  onSwipe(direction: SwipeDirection) {
    if (direction === 'left') {
      if (this.index === 0) return;
      this.index -= 1;
    }
    if (direction === 'right') {
      if (this.index >= this.numberOfDots - 1) return;
      this.index += 1;
    }
  }

  swipeEvent(event: string) {
    if (event === 'start') this.swiping = true;
    if (event === 'end') this.swiping = false;
  }

  getInnerWidth(): number {
    return window.innerWidth;
  }

  ngOnDestroy(): void {
    this.templatesSubscription.unsubscribe();
  }
}
